
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { EDCalendarViewTimeViewBooking } from '../../../../../../BaseComponents/CalendarView/Components/EDCalendarViewTimeViewBooking';
import { IBooking } from '../../../../../../BaseComponents/CalendarView/Utils/EDCalendarViewUtils';

interface IProps {
  allDayBookings: IBooking[];
  viewStartTime: Date;
  dayCount: number;
  dayWidth: number;
  timeWidth: number;
  headHeight: number;
  fallbackStartDate: string;
  fallbackEndDate: string;
  onBookingClick?: (ref: EventTarget, booking: IBooking) => void;
  isSmallScreen?: boolean;
}

const ROW_HEIGHT = 25; // Höjd mellan flerdagsbokningar i header
const BOOKING_HEIGHT = 20; // Höjd på flerdagsbokning i header
const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

const parseDateWithFallback = (
  date: Date | undefined,
  fallback: string
): Date => {
  return new Date(date || fallback);
};

const calculateDayDifference = (targetDate: Date, startDate: Date): number => {
  return Math.floor(
    (targetDate.getTime() - startDate.getTime()) / MILLISECONDS_PER_DAY
  );
};

// Vilka dagar en flerdagsbokning täcker
const calculateBookingSpan = (
  booking: IBooking,
  viewStartTime: Date,
  dayCount: number,
  fallbackStartDate: string,
  fallbackEndDate: string
) => {
  const bookingStart = parseDateWithFallback(
    booking.aktStartTime,
    fallbackStartDate
  );
  const bookingEnd = parseDateWithFallback(booking.aktEndTime, fallbackEndDate);

  const startDayDiff = calculateDayDifference(bookingStart, viewStartTime);
  const endDayDiff = calculateDayDifference(bookingEnd, viewStartTime);

  const startDayIndex = Math.max(0, startDayDiff);
  const endDayIndex = Math.min(dayCount - 1, endDayDiff);
  const spanDays = endDayIndex - startDayIndex + 1;

  return {
    startDayIndex,
    endDayIndex,
    spanDays,
    isVisible: spanDays > 0 && startDayIndex < dayCount && endDayIndex >= 0,
  };
};

// Positionerar flerdagsbokning utifrån dag
const calculateBookingPosition = (
  startDayIndex: number,
  spanDays: number,
  dayWidth: number,
  timeWidth: number,
  dayCount: number,
  isSmallScreen: boolean = false
) => {
  const totalTableWidth = timeWidth + dayCount * dayWidth;
  const leftPercentage =
    ((timeWidth + startDayIndex * dayWidth) / totalTableWidth) * 100;
  const widthPercentage = ((spanDays * dayWidth) / totalTableWidth) * 100;

  // Placera bokningar rätt i mobilvy
  const mobileAdjustment = isSmallScreen ? 0.8 : 0;

  return {
    leftPercentage: leftPercentage + mobileAdjustment,
    widthPercentage: widthPercentage - mobileAdjustment * 1.2,
  };
};

export const KalenderAllDayBookings: React.FC<IProps> = props => {
  return (
    <EDBox
      sx={{
        position: 'absolute',
        top: props.headHeight,
        left: 0,
        right: 0,
      }}
    >
      {props.allDayBookings.map((booking, index) => {
        const span = calculateBookingSpan(
          booking,
          props.viewStartTime,
          props.dayCount,
          props.fallbackStartDate,
          props.fallbackEndDate
        );

        if (!span.isVisible) return null;

        const position = calculateBookingPosition(
          span.startDayIndex,
          span.spanDays,
          props.dayWidth,
          props.timeWidth,
          props.dayCount,
          props.isSmallScreen
        );

        return (
          <EDBox
            key={booking.aktId || index}
            sx={{
              position: 'absolute',
              // Placerar bokningar i kolumn
              top: `${index * ROW_HEIGHT}px`,
              // Placerar flerdagsbokningar under rätt dag
              left: `${position.leftPercentage}%`,
              // bredden på flerdagsbokning (över rätt antal dagar)
              width: `${position.widthPercentage}%`,
              // Höjd på bokning
              height: `${BOOKING_HEIGHT}px`,
              overflow: 'hidden',
            }}
          >
            <EDBox
              sx={{
                position: 'relative',
                height: '100%',
                // Framtvinga konsekvent höjd - annars baseras höjd på bokningens längd
                '& > div': {
                  height: `${BOOKING_HEIGHT}px`,
                  top: '0px',
                },
              }}
            >
              <EDCalendarViewTimeViewBooking
                booking={booking}
                startTime={parseDateWithFallback(
                  booking.aktStartTime,
                  props.fallbackStartDate
                )}
                endTime={parseDateWithFallback(
                  booking.aktEndTime,
                  props.fallbackEndDate
                )}
                viewportWidth={span.spanDays * props.dayWidth}
                viewportHeight={BOOKING_HEIGHT}
                columnWidth={span.spanDays * props.dayWidth}
                selected={false}
                onBookingClick={props.onBookingClick}
              />
            </EDBox>
          </EDBox>
        );
      })}
    </EDBox>
  );
};
